import React, { useEffect, useState } from "react";
import { LabelWithInputField } from "components/common/molecules/LabelWithInputField";
import PhoneInput from "react-phone-number-input";
import LabelField from "components/common/atoms/LabelField";
import Button from "components/common/atoms/Button";
import { ApiCall } from "components/common/services/ApiServices";
import CustomNotify from "components/common/atoms/CustomNotify";
import { Link, useNavigate } from "react-router-dom";
import {
  validateForm,
  validateEmail,
  validateRequired,
  validateTextFieldAlpha,
  validatePhoneNumber,
  validateSelectField,
  validateDate,
} from "components/common/services/ValidationService";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import { GET_USER, REGISTER } from "routes/ApiEndpoints";
import {
  t,
  translate,
} from "components/CentralDataMangement/translation/Translation";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import { Option, OptionProps } from "components/common/utlis/TypeAnnotations";
import { mapToSelect } from "components/common/utlis/MapToSelect";
import Title from "components/common/atoms/Title";
import Calender from "components/common/molecules/Calender";
import { E164Number } from "libphonenumber-js";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import { useSelector } from "react-redux";
import { selectAuth } from "features/auth/AuthSlice";
import HidePassword from "static/images/Hide.svg";
import ShowPassword from "static/images/Show.svg";
import CheckBoxField from "components/common/atoms/CheckBoxField";
import InputTextfield from "components/common/atoms/InputTextField";
import { EMPLOYER } from "Constants";

interface CreateAjUserFormProps {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobNumber?: E164Number | string | undefined;
  role?: OptionProps | null;
  formStatus?: 1;
  office?: Option | null;
  additionalOffice?: Option[];
  dateOfJoining: Date | null;
  experience: number | null;
  formType: string;
  designation?: Option | null;
  amOromOffices?: Option[];
  preferredLanguages?: Option | null;
  brightId: string | number | null;
  brightRoles: Option[],
  brightUserName: string;
  brightPassword: string;
  isBright: boolean;
}

interface ValidationRules {
  [key: string]: Function[];
}

const CreateUserPage = () => {
  const navigate = useNavigate();
  const userAuth = useSelector(selectAuth);
  const [passwordType, setPasswordType] = useState("password");
  const togglePassword = () => {
    if (passwordType === "password") {
      setPasswordType("text");
      return;
    }
    setPasswordType("password");
  };

  const initialErrors: { [key: string]: string } = {};
  const [errors, setErrors] = useState<{ [key: string]: string }>(
    initialErrors
  );
  const [recruiterId, setRecruiterId] = useState();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateAjUserFormProps>({
    firstName: "",
    lastName: "",
    email: "",
    mobNumber: "" as E164Number,
    role: null,
    formStatus: 1,
    office: null,
    designation: null,
    additionalOffice: [],
    dateOfJoining: new Date(),
    experience: 0,
    formType: "user",
    amOromOffices: [],
    preferredLanguages: { value: 39, label: t("Dutch, Flemish") },
    brightId: "",
    brightRoles: [],
    brightUserName: "",
    brightPassword: "",
    isBright: false
  });
  const [dropdownList, setDropdownList] = useState({
    rolesList: [] as OptionProps[],
    officeList: [] as OptionProps[],
    designations: [] as any[],
    preferredLanguagesList: [] as OptionProps[],
    brightRolesList: [] as OptionProps[],
    AMOfficeList: [] as OptionProps[],
    OMOfficeList: [] as OptionProps[],
  })

  const fetchUserDetails = async () => {
    const data = {
      template: "user",
    };
    const response = await ApiCall.service(GET_USER, "POST", data, true);
    if (response?.status === 200) {
      const filteredRoles = response?.data["roles"].filter(
        (role: any) => role.unique_name !== "CANDIDATE"
      );
      const recruiterRole = filteredRoles.find(
        (role: any) => role.unique_name === "RECRUITER"
      );
      if (recruiterRole !== undefined) {
        setRecruiterId(recruiterRole.id);
      }
      setDropdownList(prev => ({
        ...prev,
        rolesList: filteredRoles,
        officeList: mapToSelect(response?.data["offices"]),
        designations: response.data["designations"],
        preferredLanguagesList: mapToSelect(response?.data["preferredLanguages"]),
        AMOfficeList: mapToSelect(response?.data["officesWithoutAM"]),
        OMOfficeList: mapToSelect(response?.data["officesWithoutOM"]),
        brightRolesList: mapToSelect(response?.data["brightRoles"])
      }));
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const validation = (
    name: string,
    value: string | boolean,
    isSingleFieldValidation: boolean = false
  ) => {
    const validationRules: ValidationRules = {
      firstName: [validateRequired, validateTextFieldAlpha],
      lastName: [validateRequired, validateTextFieldAlpha],
      email: [validateRequired, validateEmail],
      mobNumber: [validatePhoneNumber],
      role: [validateSelectField],
      office: [validateSelectField],
      dateOfJoining: [validateDate],
      brightUserName: formData.role?.unique_name !== EMPLOYER ? [validateRequired] : []
    };

    if (formData.role?.value === recruiterId) {
      validationRules.designation = [validateSelectField];
    }

    const validationErrors = validateForm(
      { ...formData, [name]: value },
      validationRules,
      isSingleFieldValidation ? name : undefined
    );

    if (isSingleFieldValidation && Object.keys(errors).length > 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationErrors[name],
      }));
    } else {
      setErrors(validationErrors);
    }

    if (Object.keys(validationErrors).length > 0) {
      return false;
    }

    return true;
  };

  const changeHandler = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, checked, type } = event.target as HTMLInputElement;
    let newValue: string | boolean = value;

    if (type == 'checkbox') {
      newValue = checked;
    }
    setFormData((prevData) => ({ ...prevData, [name]: newValue }));

    // If changing firstName or lastName, update brightUserName
    if (name === "firstName" || name === "lastName") {
      const firstName = name === "firstName" ? value : formData.firstName;
      const lastName = name === "lastName" ? value : formData.lastName;

      const updatedUserName = `${firstName}.${lastName}`;
      setFormData((prevData) => ({
        ...prevData,
        brightUserName: updatedUserName,
      }));
    }

    validation(name, value, true);
  };

  const handlePhoneNumberChange = (e: string | E164Number | undefined) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      mobNumber: e || "",
    }));
    validation("mobNumber", e || "", true);
  };

  const handleSelectChange = (selectedOption: any, fieldName: string) => {
    if (fieldName === "role") {
      formData.designation = null;
      setErrors((prevErrors) => ({
        ...prevErrors,
        ["designation"]: "",
      }));
      setFormData((prevData) => ({
        ...prevData,
        amOromOffices: [],
        office: null,
      }));
    }
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: selectedOption,
    }));
    validation(fieldName, selectedOption, true);
  };

  const handleDateChange = (date: Date, fieldName: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: date,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();
    const { name, value } = e.target as HTMLInputElement;
    if (validation(name, value)) {
      const response = await ApiCall.service(REGISTER, "POST", formData);
      if (response?.status === 200) {
        CustomNotify({ type: "success", message: response?.msg });
        navigate("/manage-users");
      } else if (response?.status === 400) {
        setErrors({
          email: response?.errors?.email,
          mobNumber: response?.errors?.phone_number,
        });
      } else {
        CustomNotify({ type: "warning", message: response?.exception });
      }
    }
    setLoading(false);
  };

  const filterDesignations = () => {
    const roleId = formData.role?.value;
    const filteredData = dropdownList.designations.filter(
      (item: any) => item.role_id === roleId
    );
    return mapToSelect(filteredData);
  };

  // To get the default offices list
  const getDefaultOffices = () => {
    const roleId = formData.role?.value;
    const filterFunction = (officeList: OptionProps[]) =>
      dropdownList.officeList.filter(
        (item) =>
          !formData.additionalOffice?.some(
            (office) => office.value === item.value
          ) &&
          !formData.amOromOffices?.some((office) => office.value === item.value)
      );

    if (roleId === 5) {
      return filterFunction(dropdownList.AMOfficeList);
    } else if (roleId === 6) {
      return filterFunction(dropdownList.OMOfficeList);
    } else {
      return filterFunction(dropdownList.officeList);
    }
  };

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Users",
          create: true,
        },
      ]}
      renderNoAccess={true}
    >
      <Title title={t("Create user")} />
      <form onSubmit={handleSubmit}>
        <div className="form-border">
          <div className="row">
            <div className="col-sm-12 col-md-4">
              <LabelWithInputField
                isMandatory={true}
                name="firstName"
                handleChange={changeHandler}
                value={formData.firstName}
                id="fname"
                label={t("First name")}
                placeholder={t("First name")}
                error={errors.firstName}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <LabelWithInputField
                isMandatory={true}
                name="lastName"
                handleChange={changeHandler}
                value={formData.lastName}
                id="lname"
                label={t("Last name")}
                placeholder={t("Last name")}
                error={errors.lastName}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <LabelWithInputField
                isMandatory={true}
                name="email"
                handleChange={changeHandler}
                value={formData.email}
                id="email"
                label={t("Email")}
                placeholder={t("Email")}
                type="email"
                error={errors.email}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <LabelField
                title={t("Phone number")}
                isMandatory={true}
                key="PhoneInput"
              />
              <div
                className="form-control field-shadow d-flex"
                style={{ borderRadius: "0.5vw" }}
              >
                <PhoneInput
                  defaultCountry="BE" //Belgium country code
                  placeholder={t("Enter phone number")}
                  value={
                    formData.mobNumber !== undefined
                      ? (formData.mobNumber as E164Number)
                      : ("" as E164Number)
                  }
                  onChange={handlePhoneNumberChange}
                  className="w-100"
                  international
                />
              </div>
              <div className="height-20 mb-2">
                {errors.mobNumber && (
                  <span className="text-danger">{errors.mobNumber}</span>
                )}
              </div>
            </div>
            <div className="col-sm-12 col-md-4 position-relative">
              <Calender
                isMandatory={true}
                label={t("Date of joining")}
                name="dateOfJoining"
                minDate={new Date(2011, 0, 1)}
                selectedDate={formData.dateOfJoining}
                onChange={(date) =>
                  handleDateChange(date || new Date(), "dateOfJoining")
                }
                error={errors.dateOfJoining}
              />
            </div>
            <div className="col-sm-12 col-md-4 position-relative">
              <LabelWithInputField
                type="number"
                label={t("Experience")}
                name="experience"
                step={0.1}
                min={0}
                max={100}
                handleChange={changeHandler}
                value={formData.experience}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <SelectWithSearch
                title={t("Role")}
                isMandatory={true}
                search={true}
                options={dropdownList.rolesList}
                onChange={(e) => handleSelectChange(e, "role")}
                isMulti={false}
                name="role"
                value={formData.role}
                error={errors.role}
                isTranslate={true}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <SelectWithSearch
                title={t("Designation")}
                search={true}
                options={filterDesignations()}
                onChange={(e) => handleSelectChange(e, "designation")}
                isMandatory={formData.role?.value === recruiterId}
                isDisabled={
                  formData.role === null || formData.role?.value === null
                }
                isMulti={false}
                name="designation"
                value={formData.designation}
                error={errors.designation}
                isTranslate={true}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <SelectWithSearch
                title={t("Default office")}
                isMandatory={true}
                search={true}
                options={getDefaultOffices()}
                placeHolder={t("Select office")}
                onChange={(e) => handleSelectChange(e, "office")}
                isMulti={false}
                name="office"
                value={formData.office}
                error={errors.office}
                isTranslate={true}
              />
            </div>
            <div className="col-sm-12 col-md-4">
              <SelectWithSearch
                title={t("Additional office")}
                isMandatory={false}
                search={true}
                options={dropdownList.officeList.filter(
                  (item) =>
                    item.value !== formData.office?.value &&
                    !(
                      formData.amOromOffices &&
                      formData.amOromOffices.some(
                        (office) => office.value === item.value
                      )
                    )
                )}
                placeHolder={t("Select office")}
                onChange={(e) => handleSelectChange(e, "additionalOffice")}
                isMulti={true}
                name="additionalOffice"
                value={formData.additionalOffice}
                isTranslate={true}
              />
            </div>
            {formData.role && formData.role.value === 5 && (
              <div className="col-sm-12 col-md-4">
                <SelectWithSearch
                  title={t("AM offices")}
                  isMandatory={false}
                  search={true}
                  options={dropdownList.officeList.filter(
                    (item) =>
                      item.value !== formData.office?.value &&
                      !(
                        formData.additionalOffice &&
                        formData.additionalOffice.some(
                          (office) => office.value === item.value
                        )
                      ) &&
                      dropdownList.AMOfficeList &&
                      dropdownList.AMOfficeList.some(
                        (office) => office.value === item.value
                      )
                  )}
                  placeHolder={t("Select office")}
                  onChange={(e) => handleSelectChange(e, "amOromOffices")}
                  isMulti={true}
                  name="amOromOffices"
                  value={formData.amOromOffices}
                  isTranslate={true}
                />
              </div>
            )}
            {formData.role && formData.role.value === 6 && (
              <div className="col-sm-12 col-md-4">
                <SelectWithSearch
                  title={t("OM offices")}
                  isMandatory={false}
                  search={true}
                  options={dropdownList.officeList.filter(
                    (item) =>
                      item.value !== formData.office?.value &&
                      !(
                        formData.additionalOffice &&
                        formData.additionalOffice.some(
                          (office) => office.value === item.value
                        )
                      )
                  )}
                  placeHolder={t("Select office")}
                  onChange={(e) => handleSelectChange(e, "amOromOffices")}
                  isMulti={true}
                  name="amOromOffices"
                  value={formData.amOromOffices}
                  isTranslate={true}
                />
              </div>
            )}
            <div className="col-sm-12 col-md-4">
              <SelectWithSearch
                title={t("Preferred languages")}
                isMandatory={false}
                search={true}
                options={dropdownList.preferredLanguagesList}
                placeHolder={t("Select")}
                onChange={(e) => handleSelectChange(e, "preferredLanguages")}
                isMulti={false}
                className="select-field"
                name="preferredLanguages"
                value={formData.preferredLanguages}
                isTranslate={true}
              />
            </div>
            {formData.role?.unique_name != EMPLOYER &&
              <>
                <div className="col-12 marginBotttom1">
                  <CheckBoxField
                    label={t("Create user in bright")}
                    name="isBright"
                    onChangeHandler={changeHandler}
                    isChecked={formData.isBright === true}
                    className="document-checkbox"
                    lineHeight="1.1vw"
                    id="isBright"
                  />
                </div>
                {formData.isBright &&
                  <>
                    <div className="col-sm-12 col-md-4">
                      <LabelWithInputField
                        isMandatory={true}
                        name="brightUserName"
                        handleChange={changeHandler}
                        value={formData.brightUserName}
                        id="brightUserName"
                        label={t("Bright user name")}
                        placeholder={t("Bright user name")}
                        error={errors.brightUserName}
                      />
                    </div>
                    <div className="col-sm-12 col-md-4">
                      <SelectWithSearch
                        title={t("Bright roles")}
                        isMandatory={false}
                        search={true}
                        options={dropdownList.brightRolesList}
                        placeHolder={t("Select")}
                        onChange={(e) => handleSelectChange(e, "brightRoles")}
                        isMulti={true}
                        className="select-field"
                        name="brightRoles"
                        value={formData.brightRoles}
                        isTranslate={true}
                      />
                    </div>
                    <div className="col-sm-12 col-md-4">
                      <LabelField
                        title={t("Bright password")}
                        isMandatory={true}
                        htmlfor="password"
                      />
                      <div className="d-flex position-relative">
                        <InputTextfield
                          name="brightPassword"
                          handleChange={changeHandler}
                          value={formData.brightPassword}
                          id="brightPassword"
                          placeholder={t("Bright password")}
                          error={errors.userName}
                          type={passwordType}
                        />
                        <p
                          onClick={togglePassword}
                          className="position-absolute cursor-pointer eye-icon"
                        >
                          {passwordType === "password" ? (
                            <img src={HidePassword} alt={t("Hide Password")} />
                          ) : (
                            <img src={ShowPassword} alt={t("Show Password")} />
                          )}
                        </p>
                      </div>
                    </div>
                  </>
                }
              </>
            }
            {userAuth.email === "absolute-jobs-team@infanion.com" && (
              <div className="col-sm-12 col-md-6">
                <LabelWithInputField
                  isMandatory={false}
                  name="brightId"
                  handleChange={changeHandler}
                  value={formData.brightId}
                  id="brightId"
                  label={t("Bright ID")}
                  placeholder={t("Bright ID")}
                  type="text"
                />
              </div>
            )}
          </div>
        </div>
        <div className="row backPadding">
          <div className="col-md-4 align-self-center">
            <Link
              to="/manage-users"
              className=" back-btn text-decoration-underline"
            >
              {t("Back")}
            </Link>
          </div>
          <div className="col-md-8 text-end">
            {!loading ? (
              <Button
                title={t("Create user")}
                type="submit"
                className="form-button shadow-none "
              />
            ) : (
              <LoadingIcon
                iconType="bars"
                color="#ff4dae"
                className="float-end"
                width={"3vw"}
                height={"3vw"}
              />
            )}
          </div>
        </div>
      </form>
    </AccessControl>
  );
};

export default translate(CreateUserPage);
